import React, { useMemo } from "react";
import { Button, Card, CardBody, Input, Label } from "reactstrap";
import SectionHeader from "@dhn/components/molecules/section-header";
import ReactTable from "@dhn/components/molecules/data-table";
import { formatFileSize } from "./document-utils";
import DocumentsSizeProgress from "./DocumentsSizeProgress";
import StatusBadge from "@dhn/components/molecules/status-badge";

const ACCEPTED_DOCUMENT_TYPES =
  ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

const DocumentsStep = ({
  isSubmitting,
  documents,
  documentsTotalSize,
  requiredDocumentRequirements,
  documentRequirementsCompleted,
  onRequirementFileChange,
  openDocumentsModal,
  removeDocument,
  showInlineError,
}) => {
  const completedCount = requiredDocumentRequirements.filter((requirement) =>
    documents.some((document) => document.requirementKey === requirement.key),
  ).length;

  const tableData = useMemo(
    () =>
      requiredDocumentRequirements.map((requirement) => ({
        ...requirement,
        document: documents.find(
          (item) => item.requirementKey === requirement.key,
        ),
      })),
    [documents, requiredDocumentRequirements],
  );

  const columns = useMemo(
    () => [
      {
        Header: "Documento requerido",
        accessor: "label",
      },
      {
        Header: "Archivo",
        Cell: ({ original }) =>
          original.document ? (
            <div style={{ minWidth: 0 }}>
              <div className="font-weight-bold text-truncate">
                <i className="fa fa-file-text-o text-info mr-2" />
                {original.document.name}
              </div>
              <small className="text-muted">
                {formatFileSize(original.document.size)}
              </small>
            </div>
          ) : (
            <span className="text-muted">
              <i className="fa fa-paperclip mr-1" />
              Sin archivo adjunto
            </span>
          ),
      },
      {
        Header: "Estado",
        width: 120,
        Cell: ({ original }) =>
          original.document ? (
            <StatusBadge color="success" icon="fa-check">
              Completo
            </StatusBadge>
          ) : (
            <StatusBadge color="warning" icon="fa-exclamation-circle">
              Pendiente
            </StatusBadge>
          ),
      },
      {
        Header: "Acciones",
        width: 300,
        Cell: ({ original }) => {
          const inputId = `prereg-document-${original.key}`;
          return (
            <div className="d-flex align-items-center">
              <Label
                for={inputId}
                className="btn btn-primary btn-sm mb-0 mr-2"
              >
                <i className="fa fa-upload mr-1" />
                {original.document ? "Reemplazar" : "Adjuntar"}
              </Label>
              <Input
                id={inputId}
                type="file"
                accept={ACCEPTED_DOCUMENT_TYPES}
                className="d-none"
                disabled={isSubmitting}
                onChange={(event) =>
                  onRequirementFileChange(event, original.key)
                }
              />
              {original.document && (
                <>
                  <Button
                    color="secondary"
                    type="button"
                    size="sm"
                    className="btn-color-gray mr-2"
                    title="Ver documento"
                    disabled={isSubmitting}
                    onClick={() => openDocumentsModal(original.document)}
                  >
                    <i className="fa fa-eye mr-1" />
                    Ver
                  </Button>
                  <Button
                    type="button"
                    color="danger"
                    size="sm"
                    className="btn-color-danger"
                    title="Borrar documento"
                    disabled={isSubmitting}
                    onClick={() => removeDocument(original.document)}
                  >
                    <i className="fa fa-trash mr-1" />
                    Borrar
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [
      isSubmitting,
      onRequirementFileChange,
      openDocumentsModal,
      removeDocument,
    ],
  );

  return (
    <>
      <div className="prereg-step-header">
        <span className="prereg-step-index">Paso 3</span>
        <h6>Soportes y Documentos</h6>
      </div>

      <Card className="mb-3">
        <CardBody>
          <SectionHeader
            icon="fa-folder-open-o"
            title="Documentos requeridos"
            description="Adjunte un archivo por cada requisito de la solicitud."
            actions={
              <StatusBadge
                color={documentRequirementsCompleted ? "success" : "warning"}
              >
                {completedCount} de {requiredDocumentRequirements.length} completos
              </StatusBadge>
            }
          />

          <ReactTable
            data={tableData}
            columns={columns}
            defaultPageSize={Math.max(tableData.length, 1)}
            minRows={Math.max(tableData.length, 1)}
            showPagination={false}
            noDataText="Complete primero los datos de la solicitud"
            className="-striped -highlight rounded"
          />

          {!documentRequirementsCompleted && tableData.length > 0 && (
            <div className="alert alert-warning d-flex align-items-center mb-0 mt-3 py-2">
              <i className="fa fa-exclamation-triangle mr-2" />
              Complete todos los documentos para habilitar el envío de la
              solicitud.
            </div>
          )}
          {showInlineError("documents")}
          {showInlineError("documentRequirements")}
        </CardBody>
      </Card>
      <DocumentsSizeProgress totalSize={documentsTotalSize || 0} />
    </>
  );
};

export default DocumentsStep;

